import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import Navbar from "./Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faPhone,
  faLock,
  faCalendar,
  faVenusMars,
  faMapMarkerAlt,
  faBriefcase,
  faArrowRight,
  faSpinner,
  faExclamationCircle,
  faUserPlus,
  faChalkboardUser,
  faHandshake,
  faGraduationCap,
  faBullseye,
  faBuilding,
  faMessage,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedin, faGithub } from "@fortawesome/free-brands-svg-icons";

const COURSE_DOMAINS = [
  "Agriculture, Food & Environmental Sciences",
  "AI, Automation & Robotics",
  "Arts, Humanities & Social Sciences",
  "Biological & Life Sciences",
  "Business, Finance & Economics",
  "Computer Science, Data & IT",
  "Education & Professional Development",
  "Engineering & Technology",
  "Healthcare & Allied Sciences",
  "Law, Governance & Public Administration",
  "Management & Leadership",
  "Media, Communication & Journalism",
  "Medical & Pharmaceutical Sciences",
  "Safety, Security & Emergency Management",
  "Sports, Exercise & Fitness Sciences",
];

const inputStyle = {
  width: "100%",
  padding: "12px 16px 12px 42px",
  background: "rgba(11, 19, 41, 0.7)",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  borderRadius: "12px",
  color: "#ffffff",
  fontSize: "0.92rem",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "'Inter', sans-serif"
};

const selectStyle = {
  ...inputStyle,
  appearance: "none",
  cursor: "pointer"
};

const labelStyle = {
  display: "block",
  color: "#cbd5e1",
  fontSize: "0.84rem",
  fontWeight: 600,
  marginBottom: "7px",
  textAlign: "left"
};

const iconStyle = {
  position: "absolute",
  left: "15px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#64748b",
  fontSize: "0.9rem"
};

function RegistrationForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "student";

  const [activeTab, setActiveTab] = useState(initialTab);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Student Form
  const [studentData, setStudentData] = useState({
    username: "",
    email: "",
    phno: "",
    password: "",
    dob: "",
    gender: "",
    location: "",
    profession: "",
    linkedin_url: "",
    github_url: "",
  });

  // Instructor Form
  const [instForm, setInstForm] = useState({
    name: "",
    email: "",
    phone: "",
    qualification: "",
    interest: "",
    main_domain: ""
  });

  // Partner Form
  const [partnerForm, setPartnerForm] = useState({
    name: "",
    organization_name: "",
    email: "",
    role: "",
    interest: "",
    message: ""
  });

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["student", "instructor", "partner"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setError("");
    setSuccessMessage("");
  };

  // Student Account Submit
  const handleStudentSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(studentData),
      });

      if (response.status === 200 || response.ok) {
        navigate("/login");
      } else {
        const data = await response.json();
        setError(data.error || "Registration failed. Please check inputs.");
      }
    } catch (err) {
      setError("Registration error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Instructor Application Submit
  const handleInstructorSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/registrations/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "instructor", ...instForm }),
      });
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error("Server returned an unexpected response. Please ensure the backend is running and try again.");
      }
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("Your instructor application has been submitted successfully! Our team will review your application and assign credentials shortly.");
        setInstForm({ name: "", email: "", phone: "", qualification: "", interest: "", main_domain: "" });
      } else {
        setError(data.error || "Submission failed. Please try again.");
      }
    } catch (err) {
      setError("Error submitting request: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Partner Application Submit
  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/registrations/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "partner", ...partnerForm }),
      });
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/json")) {
        throw new Error("Server returned an unexpected response. Please ensure the backend is running and try again.");
      }
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("Your partnership request has been submitted successfully! Our representative will be in touch shortly.");
        setPartnerForm({ name: "", organization_name: "", email: "", role: "", interest: "", message: "" });
      } else {
        setError(data.error || "Submission failed. Please try again.");
      }
    } catch (err) {
      setError("Error submitting request: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: "#0b1329", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Navbar />

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 20px",
          background: "radial-gradient(circle at 50% 30%, rgba(37, 99, 235, 0.16) 0%, rgba(11, 19, 41, 1) 75%)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Animated Background Orbs */}
        <div
          style={{
            position: "absolute",
            width: "420px",
            height: "420px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #2563eb, #0ea5e9)",
            filter: "blur(110px)",
            opacity: 0.2,
            top: "5%",
            right: "10%",
            pointerEvents: "none"
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "380px",
            height: "380px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #06b6d4, #3b82f6)",
            filter: "blur(100px)",
            opacity: 0.18,
            bottom: "5%",
            left: "10%",
            pointerEvents: "none"
          }}
        />

        {/* Card Container */}
        <div
          style={{
            width: "100%",
            maxWidth: activeTab === "student" ? "840px" : "640px",
            background: "rgba(15, 23, 42, 0.82)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "24px",
            padding: "36px 36px 32px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.6)",
            zIndex: 1,
            transition: "max-width 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                background:
                  activeTab === "student"
                    ? "linear-gradient(135deg, #2563eb, #0284c7)"
                    : activeTab === "instructor"
                    ? "linear-gradient(135deg, #0284c7, #06b6d4)"
                    : "linear-gradient(135deg, #059669, #10b981)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 14px",
                boxShadow: "0 8px 20px rgba(37, 99, 235, 0.35)",
                transition: "background 0.3s ease"
              }}
            >
              <FontAwesomeIcon
                icon={
                  activeTab === "student"
                    ? faUserPlus
                    : activeTab === "instructor"
                    ? faChalkboardUser
                    : faHandshake
                }
                style={{ color: "#ffffff", fontSize: "1.4rem" }}
              />
            </div>
            <h2 style={{ fontFamily: "'Outfit', sans-serif", color: "#ffffff", fontSize: "1.75rem", fontWeight: 800, margin: "0 0 6px" }}>
              {activeTab === "student"
                ? "Create Student Account"
                : activeTab === "instructor"
                ? "Become an Instructor"
                : "Become an Academic / Corporate Partner"}
            </h2>
            <p style={{ color: "#94a3b8", fontSize: "0.88rem", margin: 0 }}>
              {activeTab === "student"
                ? "Join Consortium eLearning and get unlimited access to expert courses"
                : activeTab === "instructor"
                ? "Share your knowledge and join our community of world-class educators"
                : "Collaborate with Consortium to empower workforce & student potential"}
            </p>
          </div>

          {/* 3 Registration Tabs */}
          <div className="auth-tabs">
            <button
              className={`auth-tab ${activeTab === "student" ? "active" : ""}`}
              onClick={() => handleTabSwitch("student")}
            >
              <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: "6px" }} /> Student
            </button>
            <button
              className={`auth-tab ${activeTab === "instructor" ? "active" : ""}`}
              onClick={() => handleTabSwitch("instructor")}
            >
              <FontAwesomeIcon icon={faChalkboardUser} style={{ marginRight: "6px" }} /> Become an Instructor
            </button>
            <button
              className={`auth-tab ${activeTab === "partner" ? "active" : ""}`}
              onClick={() => handleTabSwitch("partner")}
            >
              <FontAwesomeIcon icon={faHandshake} style={{ marginRight: "6px" }} /> Become a Partner
            </button>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="auth-success-message">
              <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: "1.1rem" }} />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.15)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                color: "#fca5a5",
                padding: "12px 16px",
                borderRadius: "12px",
                fontSize: "0.88rem",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "20px"
              }}
            >
              <FontAwesomeIcon icon={faExclamationCircle} />
              <span>{error}</span>
            </div>
          )}

          {/* ==================== TAB 1: STUDENT REGISTRATION ==================== */}
          {activeTab === "student" && (
            <div className="auth-form-container">
              <form onSubmit={handleStudentSubmit} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                {/* Row 1: Name & Email */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faUser} style={iconStyle} />
                      <input
                        type="text"
                        name="username"
                        placeholder="John Doe"
                        value={studentData.username}
                        onChange={(e) => setStudentData({ ...studentData, username: e.target.value })}
                        required
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Email Address *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faEnvelope} style={iconStyle} />
                      <input
                        type="email"
                        name="email"
                        placeholder="john@example.com"
                        value={studentData.email}
                        onChange={(e) => setStudentData({ ...studentData, email: e.target.value })}
                        required
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                {/* Row 2: Phone & Password */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Phone Number *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faPhone} style={iconStyle} />
                      <input
                        type="tel"
                        name="phno"
                        placeholder="+1 234 567 890"
                        value={studentData.phno}
                        onChange={(e) => setStudentData({ ...studentData, phno: e.target.value })}
                        required
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Password *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faLock} style={iconStyle} />
                      <input
                        type="password"
                        name="password"
                        placeholder="••••••••"
                        value={studentData.password}
                        onChange={(e) => setStudentData({ ...studentData, password: e.target.value })}
                        required
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                {/* Row 3: DOB & Gender */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Date of Birth <span style={{ color: "#64748b" }}>(Optional)</span></label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faCalendar} style={iconStyle} />
                      <input
                        type="text"
                        name="dob"
                        placeholder="YYYY-MM-DD"
                        value={studentData.dob}
                        onChange={(e) => setStudentData({ ...studentData, dob: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Gender <span style={{ color: "#64748b" }}>(Optional)</span></label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faVenusMars} style={iconStyle} />
                      <select
                        name="gender"
                        value={studentData.gender}
                        onChange={(e) => setStudentData({ ...studentData, gender: e.target.value })}
                        style={{ ...selectStyle, color: studentData.gender ? "#fff" : "#94a3b8" }}
                      >
                        <option value="" style={{ background: "#0b1329" }}>Select Gender</option>
                        <option value="Male" style={{ background: "#0b1329", color: "#fff" }}>Male</option>
                        <option value="Female" style={{ background: "#0b1329", color: "#fff" }}>Female</option>
                        <option value="Other" style={{ background: "#0b1329", color: "#fff" }}>Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Row 4: Location & Profession */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Location <span style={{ color: "#64748b" }}>(Optional)</span></label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={iconStyle} />
                      <input
                        type="text"
                        name="location"
                        placeholder="London, UK / New York, USA"
                        value={studentData.location}
                        onChange={(e) => setStudentData({ ...studentData, location: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Profession / Field <span style={{ color: "#64748b" }}>(Optional)</span></label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faBriefcase} style={iconStyle} />
                      <input
                        type="text"
                        name="profession"
                        placeholder="Software Engineer / Student"
                        value={studentData.profession}
                        onChange={(e) => setStudentData({ ...studentData, profession: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                {/* Row 5: LinkedIn & GitHub */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>LinkedIn URL <span style={{ color: "#64748b" }}>(Optional)</span></label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faLinkedin} style={iconStyle} />
                      <input
                        type="text"
                        name="linkedin_url"
                        placeholder="https://linkedin.com/in/username"
                        value={studentData.linkedin_url}
                        onChange={(e) => setStudentData({ ...studentData, linkedin_url: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>GitHub URL <span style={{ color: "#64748b" }}>(Optional)</span></label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faGithub} style={iconStyle} />
                      <input
                        type="text"
                        name="github_url"
                        placeholder="https://github.com/username"
                        value={studentData.github_url}
                        onChange={(e) => setStudentData({ ...studentData, github_url: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: "linear-gradient(135deg, #2563eb 0%, #0284c7 100%)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "12px",
                    padding: "14px",
                    fontSize: "1rem",
                    fontWeight: 700,
                    cursor: loading ? "wait" : "pointer",
                    boxShadow: "0 8px 20px rgba(37, 99, 235, 0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    marginTop: "8px",
                    transition: "all 0.3s ease"
                  }}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Creating Account...
                    </>
                  ) : (
                    <>
                      Create Student Account <FontAwesomeIcon icon={faArrowRight} />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* ==================== TAB 2: BECOME AN INSTRUCTOR ==================== */}
          {activeTab === "instructor" && (
            <div className="auth-form-container">
              <form onSubmit={handleInstructorSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faUser} style={iconStyle} />
                      <input
                        type="text"
                        placeholder="Dr. Alex Morgan"
                        value={instForm.name}
                        required
                        onChange={(e) => setInstForm({ ...instForm, name: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Email Address *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faEnvelope} style={iconStyle} />
                      <input
                        type="email"
                        placeholder="alex@university.edu"
                        value={instForm.email}
                        required
                        onChange={(e) => setInstForm({ ...instForm, email: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Contact / Phone *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faPhone} style={iconStyle} />
                      <input
                        type="tel"
                        placeholder="+1 234 567 890"
                        value={instForm.phone}
                        required
                        onChange={(e) => setInstForm({ ...instForm, phone: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Qualification *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faGraduationCap} style={iconStyle} />
                      <select
                        value={instForm.qualification}
                        required
                        onChange={(e) => setInstForm({ ...instForm, qualification: e.target.value })}
                        style={{ ...selectStyle, color: instForm.qualification ? "#fff" : "#94a3b8" }}
                      >
                        <option value="" style={{ background: "#0b1329" }}>Select Qualification</option>
                        <option value="PhD / Doctorate" style={{ background: "#0b1329", color: "#fff" }}>PhD / Doctorate</option>
                        <option value="Masters Degree" style={{ background: "#0b1329", color: "#fff" }}>Masters Degree</option>
                        <option value="Bachelors Degree" style={{ background: "#0b1329", color: "#fff" }}>Bachelors Degree</option>
                        <option value="Professional Certification" style={{ background: "#0b1329", color: "#fff" }}>Professional Certification</option>
                        <option value="Industry Expert" style={{ background: "#0b1329", color: "#fff" }}>Industry Expert</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Main Domain *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faGraduationCap} style={iconStyle} />
                      <select
                        value={instForm.main_domain}
                        required
                        onChange={(e) => setInstForm({ ...instForm, main_domain: e.target.value })}
                        style={{ ...selectStyle, color: instForm.main_domain ? "#fff" : "#94a3b8" }}
                      >
                        <option value="" style={{ background: "#0b1329" }}>Select Domain</option>
                        {COURSE_DOMAINS.map((d) => (
                          <option key={d} value={d} style={{ background: "#0b1329", color: "#fff" }}>{d}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Area of Interest / Specialty *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faBullseye} style={iconStyle} />
                      <input
                        type="text"
                        placeholder="e.g. Deep Learning, Data Analytics"
                        value={instForm.interest}
                        required
                        onChange={(e) => setInstForm({ ...instForm, interest: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: "linear-gradient(135deg, #0284c7 0%, #06b6d4 100%)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "12px",
                    padding: "14px",
                    fontSize: "0.98rem",
                    fontWeight: 700,
                    cursor: loading ? "wait" : "pointer",
                    boxShadow: "0 8px 20px rgba(6, 182, 212, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    marginTop: "6px",
                    transition: "all 0.3s ease"
                  }}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Submitting Application...
                    </>
                  ) : (
                    <>
                      Submit Instructor Application <FontAwesomeIcon icon={faArrowRight} />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* ==================== TAB 3: BECOME A PARTNER ==================== */}
          {activeTab === "partner" && (
            <div className="auth-form-container">
              <form onSubmit={handlePartnerSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Your Name *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faUser} style={iconStyle} />
                      <input
                        type="text"
                        placeholder="Sarah Jenkins"
                        value={partnerForm.name}
                        required
                        onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Organization Name *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faBuilding} style={iconStyle} />
                      <input
                        type="text"
                        placeholder="Global Tech University / Corp"
                        value={partnerForm.organization_name}
                        required
                        onChange={(e) => setPartnerForm({ ...partnerForm, organization_name: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div>
                    <label style={labelStyle}>Official Email *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faEnvelope} style={iconStyle} />
                      <input
                        type="email"
                        placeholder="s.jenkins@org.com"
                        value={partnerForm.email}
                        required
                        onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>Your Role *</label>
                    <div style={{ position: "relative" }}>
                      <FontAwesomeIcon icon={faBriefcase} style={iconStyle} />
                      <select
                        value={partnerForm.role}
                        required
                        onChange={(e) => setPartnerForm({ ...partnerForm, role: e.target.value })}
                        style={{ ...selectStyle, color: partnerForm.role ? "#fff" : "#94a3b8" }}
                      >
                        <option value="" style={{ background: "#0b1329" }}>Select Role</option>
                        <option value="Dean / Academic Director" style={{ background: "#0b1329", color: "#fff" }}>Dean / Academic Director</option>
                        <option value="CEO / Managing Director" style={{ background: "#0b1329", color: "#fff" }}>CEO / Managing Director</option>
                        <option value="HR / L&D Director" style={{ background: "#0b1329", color: "#fff" }}>HR / L&D Director</option>
                        <option value="Program Manager" style={{ background: "#0b1329", color: "#fff" }}>Program Manager</option>
                        <option value="Other" style={{ background: "#0b1329", color: "#fff" }}>Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Area of Partnership Interest *</label>
                  <div style={{ position: "relative" }}>
                    <FontAwesomeIcon icon={faBullseye} style={iconStyle} />
                    <input
                      type="text"
                      placeholder="e.g. Corporate Training, Degree Accreditation, Joint Research"
                      value={partnerForm.interest}
                      required
                      onChange={(e) => setPartnerForm({ ...partnerForm, interest: e.target.value })}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>
                    Message / Partnership Note <span style={{ color: "#64748b" }}>(Optional)</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <FontAwesomeIcon icon={faMessage} style={{ ...iconStyle, top: "18px", transform: "none" }} />
                    <textarea
                      placeholder="Share additional details about your institution and partnership vision..."
                      value={partnerForm.message}
                      onChange={(e) => setPartnerForm({ ...partnerForm, message: e.target.value })}
                      rows={3}
                      style={{
                        ...inputStyle,
                        paddingTop: "12px",
                        resize: "vertical",
                        minHeight: "75px"
                      }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: "12px",
                    padding: "14px",
                    fontSize: "0.98rem",
                    fontWeight: 700,
                    cursor: loading ? "wait" : "pointer",
                    boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    marginTop: "6px",
                    transition: "all 0.3s ease"
                  }}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Submitting Partnership Request...
                    </>
                  ) : (
                    <>
                      Submit Partnership Request <FontAwesomeIcon icon={faArrowRight} />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}

          {/* Bottom Login Link */}
          <div style={{ textAlign: "center", marginTop: "24px", paddingTop: "18px", borderTop: "1px solid rgba(255, 255, 255, 0.08)" }}>
            <span style={{ color: "#94a3b8", fontSize: "0.88rem" }}>
              Already have an account?{" "}
              <Link to="/login" style={{ color: "#38bdf8", fontWeight: 700, textDecoration: "none" }}>
                Sign In
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegistrationForm;
